import React, { memo, useCallback, useEffect, useImperativeHandle, useRef } from "react";

import { createSlideShow, ISlideShow } from "../slideshow/slideshow-js";

import "../slideshow/slideshow.css";
import "./slideshow-preview.css";
import { ISpreadSlideShowItem } from "./spread-slideshow-item";

const CONFIRMED_SINGLE_CLICK_DELAY_MS = 300;

/** Use this type for typing the ref result */
export interface ISlidesShowPreviewRef {
	el: HTMLDivElement;
	previewEl: HTMLDivElement;
	slideShow: ISlideShow<ISpreadSlideShowItem>;
}

/**
 * Single-click side effect that must wait until the preview knows the first click
 * was not part of a double click.
 */
type ConfirmedSingleClickAction = () => void;

/**
 * Handles the immediate part of a preview click.
 *
 * Return `undefined` when the whole click handling is safe to run immediately.
 * Return a function when only the final single-click side effect must be delayed
 * until the double-click guard window expires.
 */
type SlideshowPreviewClickHandler = (
	e: React.MouseEvent<HTMLDivElement>,
) => ConfirmedSingleClickAction | undefined;

/**
 * Keep double click handling on the document so iOS can still surface the event,
 * while the preview owns the timeout/cancellation bookkeeping for ambiguous
 * single-vs-double click cases.
 */
function useDocumentDoubleClick(
	ref: React.RefObject<HTMLElement | null>,
	cancelPendingConfirmedSingleClick: () => void,
	onDoubleClick: (e: MouseEvent) => void = () => undefined,
) {
	useEffect(() => {
		const handleDoubleClick = (e: MouseEvent) => {
			const clickRef = ref.current;
			if (!clickRef) return;

			// Clicked on the target?
			const eventPath = e.composedPath?.() ?? [];
			const clickedInsideTarget = eventPath.length
				? eventPath.includes(clickRef)
				: clickRef.contains(e.target as Node | null);

			if (!clickedInsideTarget) return;

			cancelPendingConfirmedSingleClick();
			onDoubleClick(e);
		};

		document.addEventListener("dblclick", handleDoubleClick);

		return () => {
			document.removeEventListener("dblclick", handleDoubleClick);
		};
	}, [cancelPendingConfirmedSingleClick, ref, onDoubleClick]);
}

export const SlideshowPreview = memo(
	({
		onClick,
		onDoubleClick,
		onMouseMove,
		ref,
	}: {
		onClick?: SlideshowPreviewClickHandler;
		onMouseMove?: React.DOMAttributes<HTMLDivElement>["onMouseMove"];
		onDoubleClick: (e: MouseEvent) => void;
		ref?: React.Ref<ISlidesShowPreviewRef | null>;
	}) => {
		const slideShowElRef = useRef<HTMLDivElement>(null);
		const slideShowPreviewRef = useRef<HTMLDivElement>(null);
		const pendingConfirmedSingleClickTimeoutRef = useRef<
			ReturnType<typeof setTimeout> | undefined
		>(undefined);

		const cancelPendingConfirmedSingleClick = useCallback(() => {
			if (!pendingConfirmedSingleClickTimeoutRef.current) return;

			clearTimeout(pendingConfirmedSingleClickTimeoutRef.current);
			pendingConfirmedSingleClickTimeoutRef.current = undefined;
		}, []);

		useEffect(() => cancelPendingConfirmedSingleClick, [cancelPendingConfirmedSingleClick]);

		useDocumentDoubleClick(
			slideShowPreviewRef,
			cancelPendingConfirmedSingleClick,
			onDoubleClick,
		);

		const handleClick = useCallback(
			(event: React.MouseEvent<HTMLDivElement>) => {
				cancelPendingConfirmedSingleClick();

				if (!onClick || event.detail > 1) return;

				// `onClick` handles everything that is safe to do immediately.
				// A returned callback means "run this single-click side effect only if
				// no double click follows within the guard window".
				const confirmedSingleClickAction = onClick(event);

				if (!confirmedSingleClickAction) return;

				pendingConfirmedSingleClickTimeoutRef.current = setTimeout(() => {
					pendingConfirmedSingleClickTimeoutRef.current = undefined;
					confirmedSingleClickAction();
				}, CONFIRMED_SINGLE_CLICK_DELAY_MS);
			},
			[cancelPendingConfirmedSingleClick, onClick],
		);

		/** Expose SlideShow object via ref */
		useImperativeHandle(
			ref,
			() => {
				if (!slideShowElRef.current || !slideShowPreviewRef.current) {
					throw new Error("useImperativeHandle: No SlideShowPreview element");
				}

				const slideShow = createSlideShow(
					slideShowElRef.current,
				) as ISlideShow<ISpreadSlideShowItem>;

				// Make slideshow available on console in 'Debug' mode
				// Assuming only one slideshow item is used, else global variable overridden
				if (eXpress.isDebugMode) {
					(window as any)._slideShow = slideShow;
				}

				return {
					el: slideShowElRef.current,
					previewEl: slideShowPreviewRef.current,
					slideShow,
				};
			},
			[],
		);

		return (
			// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
			<div
				className="slideshow-preview"
				role="img"
				ref={slideShowPreviewRef}
				onClick={handleClick}
				onMouseMove={onMouseMove}
			>
				{/* Parent fullscreen div needed for drag handles */}
				<div ref={slideShowElRef} />
			</div>
		);
	},
);
SlideshowPreview.displayName = "SlideshowPreview";
